import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";

const ACTION_LABELS = {
  user_registered: "User registered",
  user_login: "Login",
  smtp_configured: "SMTP configured",
  requisition_created: "Requisition raised",
  requisition_accepted: "Requisition accepted",
  requisition_rejected: "Requisition rejected",
  candidate_applied: "Candidate applied",
  ai_screened: "AI screened",
  interview_scheduled_round2: "R2 scheduled",
  interview_scheduled_final: "Final scheduled",
  round2_feedback_submitted: "R2 feedback",
  panel_feedback_submitted: "Panel feedback",
  decision_proceed: "Decision · proceed",
  decision_reject: "Decision · reject",
  decision_select: "Decision · select",
  offer_drafted: "Offer drafted",
};

const ACTION_TONE = {
  user_login: "border-info text-info",
  user_registered: "border-info text-info",
  smtp_configured: "border-quiet text-quiet",
  requisition_created: "border-klein text-klein",
  requisition_accepted: "border-success text-success",
  requisition_rejected: "border-error text-error",
  candidate_applied: "border-klein text-klein",
  ai_screened: "border-warning text-warning",
  interview_scheduled_round2: "border-warning text-warning",
  interview_scheduled_final: "border-klein text-klein",
  round2_feedback_submitted: "border-warning text-ink",
  panel_feedback_submitted: "border-klein text-ink",
  decision_proceed: "border-klein text-klein",
  decision_reject: "border-error text-error",
  decision_select: "border-success text-success",
  offer_drafted: "border-success text-success",
};

const TONE_HEX = {
  "border-info text-info": "border-[#3B82F6] text-[#3B82F6]",
  "border-quiet text-quiet": "border-[#9CA3AF] text-[#52525B]",
  "border-klein text-klein": "border-[#002FA7] text-[#002FA7]",
  "border-success text-success": "border-[#10B981] text-[#10B981]",
  "border-error text-error": "border-[#EF4444] text-[#EF4444]",
  "border-warning text-warning": "border-[#F59E0B] text-[#F59E0B]",
  "border-warning text-ink": "border-[#F59E0B] text-[#0F0F0F]",
  "border-klein text-ink": "border-[#002FA7] text-[#0F0F0F]",
};

export default function Activity() {
  const [items, setItems] = useState([]);
  const [actorFilter, setActorFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (actorFilter) params.actor_email = actorFilter;
      if (actionFilter) params.action = actionFilter;
      const r = await api.get("/activity", { params });
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [actorFilter, actionFilter]);

  const actors = useMemo(() => {
    const s = new Set();
    items.forEach((i) => i.actor_email && s.add(i.actor_email));
    return Array.from(s).sort();
  }, [items]);

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="activity-page">
      <div className="flex items-end justify-between border-b border-rule pb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
            Audit · Admin
          </div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">Activity log.</h1>
          <p className="text-quiet text-sm mt-3 max-w-xl">
            Every login, requisition decision, candidate application, AI screening, scheduling,
            feedback submission, decision, and offer is recorded here in real time.
          </p>
        </div>
        <button
          data-testid="reload-activity"
          onClick={load}
          className="border border-ink hover:bg-paper px-4 py-2 text-sm font-medium"
        >
          Refresh
        </button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-rule p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Filter by user</div>
          <select
            data-testid="filter-actor"
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            className="mt-2 w-full border border-rule px-3 py-2 bg-white text-sm focus:outline-none focus:border-klein"
          >
            <option value="">{`All users (${actors.length})`}</option>
            {actors.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div className="bg-white border border-rule p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Filter by action</div>
          <select
            data-testid="filter-action"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="mt-2 w-full border border-rule px-3 py-2 bg-white text-sm focus:outline-none focus:border-klein"
          >
            <option value="">All actions</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div className="bg-white border border-rule p-4">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">Total events</div>
          <div className="font-mono text-3xl text-ink mt-2 tracking-tight" data-testid="activity-count">
            {String(items.length).padStart(3, "0")}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-rule">
        {loading && <div className="px-5 py-12 text-center text-quiet text-sm">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="px-5 py-16 text-center">
            <div className="font-serif text-2xl text-ink">No events match your filters</div>
            <div className="text-sm text-quiet mt-2">Try clearing the filters above.</div>
          </div>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rule">
                <Th>When</Th>
                <Th>Actor</Th>
                <Th>Role</Th>
                <Th>Action</Th>
                <Th>Target / Detail</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const tone = TONE_HEX[ACTION_TONE[it.action]] || "border-[#D2D2D0] text-[#52525B]";
                const meta = it.meta || {};
                return (
                  <tr key={it.id} className="border-b border-rule hover:bg-paper" data-testid={`activity-row-${it.id}`}>
                    <Td className="font-mono text-xs text-quiet whitespace-nowrap">
                      {new Date(it.created_at).toLocaleString()}
                    </Td>
                    <Td>
                      <div className="text-ink">{it.actor_name || it.actor_email || "—"}</div>
                      <div className="text-xs text-quiet font-mono">{it.actor_email}</div>
                    </Td>
                    <Td>
                      <span className="px-2 py-0.5 border border-[#D2D2D0] text-[10px] font-mono uppercase tracking-wider text-quiet">
                        {it.actor_role || "—"}
                      </span>
                    </Td>
                    <Td>
                      <span className={`inline-flex items-center px-2 py-1 border text-[10px] font-mono uppercase tracking-[0.15em] bg-white ${tone}`}>
                        {ACTION_LABELS[it.action] || it.action}
                      </span>
                    </Td>
                    <Td className="text-xs text-ink">
                      {meta.title && <div>{meta.title}</div>}
                      {meta.candidate && <div className="text-quiet">Candidate · {meta.candidate}</div>}
                      {meta.code && <div className="font-mono text-quiet">{meta.code}</div>}
                      {meta.requisition && <div className="text-quiet">Req · {meta.requisition}</div>}
                      {meta.overall != null && <div className="font-mono">Score · {meta.overall}</div>}
                      {meta.recommendation && <div className="font-mono uppercase">Rec · {meta.recommendation}</div>}
                      {meta.scheduled_at && <div className="text-quiet">{new Date(meta.scheduled_at).toLocaleString()}</div>}
                      {meta.ctc != null && <div className="font-mono">{meta.ctc} LPA</div>}
                      {meta.comment && <div className="italic text-quiet truncate max-w-md">"{meta.comment}"</div>}
                      {meta.role && <div className="font-mono uppercase text-quiet">{meta.role}</div>}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const Th = ({ children }) => (
  <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-[0.15em] text-quiet">{children}</th>
);
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
